import React from 'react';
import { motion } from 'framer-motion';
import StarIcon from '@mui/icons-material/Star';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import VerifiedIcon from '@mui/icons-material/Verified';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import { format } from 'date-fns';
import { pl } from 'date-fns/locale';

import { Review } from '../../models/Review';

interface ReviewCardProps {
  review: Review;
}

const ReviewCard: React.FC<ReviewCardProps> = ({ review }) => {
  // Konwersja string do Date jeśli potrzebne
  const reviewDate = review.createdAt instanceof Date 
    ? review.createdAt 
    : new Date(review.createdAt);

  return (
    <motion.div 
      className="bg-white rounded-lg border border-gray-100 p-5 shadow-sm hover:shadow-md transition-shadow"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4 }}
      whileHover={{ y: -3 }}
    >
      {/* Nagłówek recenzji */}
      <div className="flex justify-between items-start mb-3">
        <div>
          <h3 className="font-semibold text-gray-900">
            {review.title || "Recenzja produktu"}
          </h3>
          <div className="flex items-center mt-1">
            <div className="text-amber-500 flex">
              {[...Array(5)].map((_, i) => (
                i < review.rating
                  ? <StarIcon key={i} fontSize="small" />
                  : <StarOutlineIcon key={i} fontSize="small" />
              ))}
            </div>
            <span className="ml-2 text-sm text-gray-500">
              {review.rating}/5
            </span>
          </div>
        </div>

        {/* Odznaki */}
        <div className="flex space-x-2">
          {review.verified && (
            <div className="bg-green-50 text-green-700 text-xs px-2 py-1 rounded-full flex items-center">
              <VerifiedIcon fontSize="small" className="mr-1" />
              <span>Zweryfikowana</span>
            </div>
          )}
          {review.hasPurchased && (
            <div className="bg-blue-50 text-blue-700 text-xs px-2 py-1 rounded-full flex items-center">
              <ShoppingBagIcon fontSize="small" className="mr-1" />
              <span>Potwierdzona zakupem</span>
            </div>
          )}
        </div>
      </div>

      {/* Treść recenzji */}
      {review.comment && (
        <div className="text-gray-700 my-3">
          <p>{review.comment}</p>
        </div>
      )}

      {/* Stopka */}
      <div className="mt-4 pt-3 border-t border-gray-100 flex justify-between items-center text-xs text-gray-500">
        <div className="flex items-center">
          <span className="font-medium">{review.userName || "Użytkownik"}</span>
        </div>
        <time dateTime={reviewDate.toISOString()}>
          {format(reviewDate, "d MMMM yyyy", { locale: pl })}
        </time>
      </div>
    </motion.div>
  );
};

export default ReviewCard;
